import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import Contact from './Contact';
import { services } from '../mock';

const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = services.find((s) => s.slug === slug);

  // Always start at the top when navigating between service pages
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0e0e0e] text-white px-6">
        <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Serviço não encontrado
        </h1>
        <button
          onClick={() => navigate('/')}
          className="mt-4 px-6 py-3 bg-[#C99A36] text-black font-semibold tracking-[0.15em] rounded-full hover:bg-[#E8C16A] transition-colors"
        >
          Voltar para o início
        </button>
      </div>
    );
  }

  const others = services.filter((s) => s.slug !== slug);

  return (
    <div className="bg-white">
      <Header />

      {/* Hero banner */}
      <section
        className="relative w-full h-[62vh] min-h-[420px] overflow-hidden mt-[80px] flex items-center justify-center"
      >
        <img
          src={service.heroImage}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70" />

        <div className="relative z-10 text-center px-6">
          {/* SETA icon mark */}
          <div className="flex justify-center mb-6">
            <SetaIconMark size={78} />
          </div>
          <h1
            className="text-white font-black text-[34px] md:text-[52px] lg:text-[62px] tracking-[0.06em] uppercase drop-shadow-lg"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {service.title}
          </h1>
        </div>
      </section>

      {/* Description strip - textured gold */}
      <section
        className="relative"
        style={{
          background:
            'linear-gradient(180deg, #C99A36 0%, #b3862a 50%, #8f6a1c 100%)',
        }}
      >
        <div className="max-w-5xl mx-auto px-6 py-14 text-center">
          <p
            className="text-white text-[16px] md:text-[18px] leading-[1.75] tracking-wide"
            style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 400 }}
          >
            {service.description}
          </p>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-[#f5f5f3] py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <p
              className="text-[#C99A36] tracking-[0.4em] text-[12px] font-semibold mb-4"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              O QUE FAZEMOS
            </p>
            <h2
              className="text-[#1a1a1a] text-[34px] md:text-[42px] font-bold tracking-tight"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Serviços
            </h2>
            <div className="mx-auto mt-4 w-24 h-[3px] bg-[#C99A36]" />
          </div>

          <ul className="space-y-3">
            {service.items.map((item, i) => (
              <li
                key={i}
                className="group flex items-start gap-4 bg-white px-6 py-5 border-l-4 border-[#C99A36] shadow-sm hover:shadow-lg hover:translate-x-1 transition-all duration-300"
              >
                <span className="mt-1 flex-shrink-0">
                  <SetaBullet />
                </span>
                <span
                  className="text-[#2a2a2a] text-[15px] md:text-[16px] leading-[1.6] tracking-wide group-hover:text-[#1a1a1a]"
                  style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
                >
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Other services */}
      <section
        className="relative py-20 px-6"
        style={{
          background:
            'linear-gradient(180deg, #C99A36 0%, #b3862a 60%, #8f6a1c 100%)',
        }}
      >
        <div className="max-w-5xl mx-auto text-center">
          <h3
            className="text-white text-[28px] md:text-[34px] font-bold tracking-tight mb-3 uppercase"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Outros Serviços
          </h3>
          <div className="mx-auto w-20 h-[3px] bg-white/70 mb-10" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {others.map((s) => (
              <Link
                key={s.slug}
                to={`/servicos/${s.slug}`}
                className="group bg-[#0e0e0e]/85 hover:bg-[#0e0e0e] backdrop-blur px-6 py-5 flex items-center justify-between gap-3 text-white transition-all duration-300 hover:scale-[1.02]"
              >
                <span
                  className="text-[13px] md:text-[14px] tracking-[0.14em] font-semibold uppercase"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {s.title}
                </span>
                <ChevronRight
                  size={20}
                  className="text-[#E8C16A] group-hover:translate-x-1 transition-transform"
                />
              </Link>
            ))}

            <Link
              to="/"
              className="group bg-[#0e0e0e]/85 hover:bg-[#0e0e0e] backdrop-blur px-6 py-5 flex items-center justify-between gap-3 text-white transition-all duration-300 hover:scale-[1.02] md:col-span-3"
            >
              <span className="flex items-center gap-2">
                <Home size={18} className="text-[#E8C16A]" />
                <span
                  className="text-[13px] md:text-[14px] tracking-[0.14em] font-semibold uppercase"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Página Inicial
                </span>
              </span>
              <ChevronRight
                size={20}
                className="text-[#E8C16A] group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact form (reuses the site contact section) */}
      <div id="contato-service">
        <Contact intro="Entre em contato para que possamos começar a trabalhar juntos:" />
      </div>

      <Footer />
    </div>
  );
};

// Small gold triangular bullet marker (matches SETA identity)
const SetaBullet = () => (
  <svg width="16" height="20" viewBox="0 0 16 20" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bulletGold" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#E8C16A" />
        <stop offset="100%" stopColor="#8a6716" />
      </linearGradient>
    </defs>
    <polygon points="2,2 2,18 14,10" fill="url(#bulletGold)" />
  </svg>
);

// Large SETA icon mark used on service page hero
const SetaIconMark = ({ size = 80 }) => (
  <svg width={size} height={size * 1.2} viewBox="0 0 80 96" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="markGold" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#F0CB76" />
        <stop offset="60%" stopColor="#C99A36" />
        <stop offset="100%" stopColor="#7d5d12" />
      </linearGradient>
      <linearGradient id="markSilver" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#eaeaea" />
        <stop offset="60%" stopColor="#a5a5a5" />
        <stop offset="100%" stopColor="#5c5c5c" />
      </linearGradient>
    </defs>
    {/* Silver outer triangle */}
    <polygon points="8,10 8,86 72,48" fill="url(#markSilver)" />
    {/* Inner gold triangle */}
    <polygon points="20,22 20,74 60,48" fill="url(#markGold)" />
    {/* Small top arrow */}
    <polygon points="52,2 66,2 59,14" fill="url(#markSilver)" />
    <polygon points="54,2 64,2 59,11" fill="url(#markGold)" />
  </svg>
);

export default ServicePage;
